/**
 * Toast host.
 *
 * Replaces the `showToast` / `showSuccessToast` / `showErrorToast` trio, each of
 * which built and appended its own DOM node and left them behind on navigation.
 */

import { Icon } from '../common';
import { useNotifications } from '../../context/NotificationContext';
import { NOTIFICATION_TYPES } from '../../domain/notifications';

const TOAST_STYLES = {
  [NOTIFICATION_TYPES.SUCCESS]: 'border-emerald-500/40 bg-emerald-500/15 text-emerald-200',
  [NOTIFICATION_TYPES.DANGER]: 'border-red-500/40 bg-red-500/15 text-red-200',
  [NOTIFICATION_TYPES.WARNING]: 'border-amber-500/40 bg-amber-500/15 text-amber-200',
  [NOTIFICATION_TYPES.INFO]: 'border-cyan-500/40 bg-cyan-500/15 text-cyan-200',
};

const TOAST_ICONS = {
  [NOTIFICATION_TYPES.SUCCESS]: 'check-circle-2',
  [NOTIFICATION_TYPES.DANGER]: 'alert-octagon',
  [NOTIFICATION_TYPES.WARNING]: 'alert-triangle',
  [NOTIFICATION_TYPES.INFO]: 'info',
};

export function ToastHost() {
  const { toasts, dismissToast } = useNotifications();
  if (toasts.length === 0) return null;

  return (
    <div
      className="pointer-events-none fixed bottom-4 left-1/2 z-[400] flex w-full max-w-md -translate-x-1/2 flex-col gap-2 px-4"
      role="status"
      aria-live="polite"
    >
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`pointer-events-auto flex items-start gap-3 rounded-xl border p-3 shadow-2xl backdrop-blur ${
            TOAST_STYLES[toast.type] || TOAST_STYLES[NOTIFICATION_TYPES.INFO]
          }`}
        >
          <Icon name={TOAST_ICONS[toast.type] || 'info'} size={18} className="mt-0.5 shrink-0" />
          <p className="flex-1 text-sm font-bold leading-relaxed">{toast.message}</p>
          <button
            type="button"
            onClick={() => dismissToast(toast.id)}
            className="shrink-0 opacity-70 transition-opacity hover:opacity-100"
            aria-label="إغلاق التنبيه"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}
